import { createCrossDomainAuthRoute } from '@axo/auth/strategies/cross-domain/util/create-cross-domain-auth-route';
import { ConfigService } from '@axo/config';
import {
  Mutators,
  reloadData,
  useBackendSync,
} from '@axo/form/data-access/backend-sync';
import { setStartedAtUrlIfNotSet } from '@axo/form/data-access/tracking';
import { Product } from '@axo/shared/data-access/types';
import { DataSharingStatus, MarketCountryCode } from '@axo/shared/types';
import { captureException } from '@sentry/browser';
import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { RedirectUrl, Step } from '../types';
import { useTracking } from './tracking/useTracking';
import { useAxoFormData } from './useAxoFormData';
import { useCustomerAnalytics } from './useCustomerAnalytics';
import { useSteps, UseStepsProps } from './useSteps/useSteps';

export interface AxoFormOptions<Data extends Record<string, unknown>> {
  product?: Product;
  mutators: Mutators<Data>;
  steps: Step[];
  marketCountry: MarketCountryCode;
  isPre?: boolean;
  formRef?: RefObject<HTMLFormElement>;
  configureSteps?: (data: Data) => void;
  dataSharingStatus?: DataSharingStatus;
  baseUrl: string;
  redirectUrl?: RedirectUrl;
  onCompleted?: (args?: { searchParams?: string }) => void;
  onStepChangeScrollTo?: UseStepsProps['onStepChangeScrollTo'];
}

export const useAxoForm = <Data extends Record<string, unknown>>({
  product,
  mutators,
  baseUrl,
  steps,
  redirectUrl,
  marketCountry,
  isPre,
  formRef,
  configureSteps,
  dataSharingStatus,
  onCompleted,
  onStepChangeScrollTo,
}: AxoFormOptions<Data>) => {
  const brand = ConfigService.get('global').brand;

  const sync = useBackendSync<Data>({
    marketCountry,
    product,
    brand,
    initialSnapshotStepName: steps[0]?.name,
    mutators,
    baseUrl,
  });

  const { axoFormData, updateAxoFormData } = useAxoFormData<Data>({
    defaultValues: sync.reloadedData,
    configureSteps,
  });

  const tracking = useTracking({
    product,
    data: axoFormData,
    baseUrl,
  });

  const { step, setStep, StepComponent, numberOfCompletedSteps } = useSteps({
    initialized: sync.initialized,
    formRef,
    steps,
    marketCountry,
    product,
    reloadedData: sync.reloadedData,
    isPre,
    dataSharingStatus,
    onStepChangeScrollTo,
  });

  useCustomerAnalytics({ data: sync });

  useEffect(() => {
    if (!sync.initialized) return;
    onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, sync.initialized]);

  const onViewed = async () => {
    await tracking.onViewed({
      step: step,
      formData: axoFormData,
      customerId: sync.getPreconditions().customerId,
    });
  };

  const onInitiated = useCallback(async () => {
    await tracking.onInitiated({
      step: step,
      formData: axoFormData,
      customerId: sync.getPreconditions().customerId,
    });
  }, [step, axoFormData, tracking, sync]);

  const postForm = useCallback(
    async (data: Data) => {
      const updatedData = updateAxoFormData(data);
      await sync.queueImmediate(updatedData);
    },
    [sync, updateAxoFormData]
  );

  const submitApplication = useCallback(
    async (data: Data) => {
      const finalFormData = {
        ...axoFormData,
        ...data,
        ...mutators.toApiLastSync?.({ ...axoFormData, ...data }),
      };

      await postForm(finalFormData);
      await tracking.completeApplication(
        steps[step - 1].name,
        {
          formData: finalFormData,
          customerId: sync.getPreconditions().customerId,
        },
        product
      );

      //

      if (!redirectUrl && !onCompleted) {
        captureException(new Error('No redirect URL / onCompleted provided'));
        console.error('Redirect URL / onCompleted is missing.');
        return;
      }

      /**
       * `onComplete` callback (external route handling – preferred method)
       */
      if (onCompleted) {
        reloadData.reset(); // FIXME @lucas is this needed? can't we flush on create?
        onCompleted();
        return;
      }

      /**
       * static `redirectUrl` – augmented with query params
       */
      const { customerJWT, applicationId } = reloadData.get(product);

      if (redirectUrl && customerJWT && applicationId) {
        const url = createCrossDomainAuthRoute({
          baseUrl: redirectUrl,
          data: { token: customerJWT, applicationId },
          forLegacy: true,
        });

        reloadData.reset();

        window.location.href = url.toString();
      } else {
        captureException(
          new Error('Missing data for redirect on application submit')
        );
        console.error('Missing data for redirect on application submit');
      }
    },
    [
      axoFormData,
      mutators,
      postForm,
      redirectUrl,
      step,
      steps,
      tracking,
      product,
      sync,
      onCompleted,
    ]
  );

  const submitStep = useCallback(
    async (data: Data, isPre?: boolean) => {
      setStartedAtUrlIfNotSet();
      await postForm(data);
      await tracking.completeStep(
        steps[step - 1].name,
        {
          step: step,
          formData: axoFormData,
          customerId: sync.getPreconditions().customerId,
        },
        product
      );

      if (isPre) return;

      configureSteps?.({ ...axoFormData, ...data });
      setStep(step + 1);
    },
    [
      axoFormData,
      configureSteps,
      postForm,
      setStep,
      step,
      steps,
      tracking,
      product,
      sync,
    ]
  );

  const syncData = useCallback(
    async (data: Data) => {
      const updatedData = updateAxoFormData(data);

      if (sync.getPreconditions().loanApplicationId) {
        return sync.queue(updatedData);
      }
    },
    [sync, updateAxoFormData]
  );

  const reloadSyncData = useCallback(async () => sync.reload(), [sync]);

  return useMemo(
    () => ({
      onInitiated,
      submitStep,
      submitApplication,
      StepComponent,
      step,
      setStep,
      axoFormData,
      syncData,
      reloadSyncData,
      initialized: sync.initialized,
      numberOfCompletedSteps,
    }),
    [
      onInitiated,
      submitStep,
      submitApplication,
      StepComponent,
      step,
      setStep,
      axoFormData,
      syncData,
      reloadSyncData,
      sync.initialized,
      numberOfCompletedSteps,
    ]
  );
};
